import { useCallback, useContext } from 'react';
import { PagedDialog } from '@components';
import { TShipmentOrder } from '@types';
import { EditShipment } from '@state';
import { useRecoilValue } from 'recoil';
import TimerIcon from '@mui/icons-material/TimerOutlined';
import ArrowUpIcon from '@mui/icons-material/NorthEast';
import ArrowDownIcon from '@mui/icons-material/SouthEast';
import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import SearchIcon from '@mui/icons-material/ManageSearchOutlined';
import TruckIcon from '@mui/icons-material/LocalShippingOutlined';
import LocationIcon from '@mui/icons-material/LocationOnOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import { ShipperIcon } from '@img/icons';
import { isDateWithin60Days, groupCargosByStop } from '@utils';
import { EditShipmentContext } from './EditShipment.Context';
import styles from './EditShipment.module.scss';

const BOOKING_TIMES_EDIT_WINDOW_TOOLTIP = 'Cannot edit after 60 days';

export const Menu = () => {
	const ctx = useContext(EditShipmentContext);
	const access = ctx?.access;
	const shipment = useRecoilValue<TShipmentOrder | null>(EditShipment);
	// Cargos collected from the same place are one stop, followed by the shipment's single drop-off.
	const getDestinations = useCallback(() => {
		const destinations: string[] = [];
		if (shipment) {
			groupCargosByStop(shipment.cargos).pickups.forEach((group) => {
				destinations.push(`${shipment.cargos[group[0]].pickup.business_name}`);
			});
			if (shipment.cargos.length) {
				destinations.push(`${shipment.cargos[shipment.cargos.length - 1].dropoff.business_name}`);
			}
		}
		return destinations;
	}, [shipment]);
	const bookingTimesEditWindowExpired = useCallback(() => {
		if (!shipment) return false;
		return shipment.cargos.every((cargo, i) => {
			const pickupExpired = !isDateWithin60Days(cargo.original_pickup_at);
			if (i === shipment.cargos.length - 1) {
				return pickupExpired && !isDateWithin60Days(cargo.original_drop_at);
			}
			return pickupExpired;
		});
	}, [shipment]);
	/* The id is the stop's ordinal, which is what each page anchors its scroll targets by. Only the
	   last destination is the drop-off; everything before it is a pick-up. */
	const stopSubItems = () => {
		const destinations = getDestinations();
		return destinations.map((destination, i) => {
			const isDropOff = i === destinations.length - 1;
			const Icon = isDropOff ? ArrowDownIcon : ArrowUpIcon;
			return <PagedDialog.MenuItem
				key={i}
				id={i}
				label={<span className={styles.subItem}><Icon style={{ fontSize: '18px' }} /><p className={styles.ellipsis}>{destination}</p></span>}
				subItem
			/>
		});
	};
	return (
		<PagedDialog.Menu>
			{access?.pages.updateETA.visible && <PagedDialog.MenuItem
				id={0}
				label={<p className={styles.subItem}><TimerIcon style={{ fontSize: '18px' }} />Manage Booking Times</p>}
				disabled={bookingTimesEditWindowExpired()}
				disabledTooltip={BOOKING_TIMES_EDIT_WINDOW_TOOLTIP}
			>
				{stopSubItems()}
			</PagedDialog.MenuItem>}
			{access?.pages.cargo.visible && <PagedDialog.MenuItem id={1} label={<p className={styles.subItem}><ShipperIcon width={18} height={18} />Cargo</p>} />}
			{access?.pages.modifiers.visible && <PagedDialog.MenuItem id={7} label={<p className={styles.subItem}><TuneIcon style={{ fontSize: '18px' }} />Modifiers</p>} />}
			{access?.pages.location.visible && <PagedDialog.MenuItem id={2} label={<p className={styles.subItem}><LocationIcon style={{ fontSize: '18px' }} />Location Details</p>}>
				{stopSubItems()}
			</PagedDialog.MenuItem>}
			{access?.pages.contactDetails.visible && <PagedDialog.MenuItem id={3} label={<p className={styles.subItem}><PeopleIcon style={{ fontSize: '18px' }} />Contact Details</p>}>
				{stopSubItems()}
			</PagedDialog.MenuItem>}
			{access?.pages.driverInfo.visible && <PagedDialog.MenuItem id={4} label={<p className={styles.subItem}><AssignmentIcon style={{ fontSize: '18px' }} />Driver Information</p>}>
				{stopSubItems()}
			</PagedDialog.MenuItem>}
			{access?.pages.referenceNumbers.visible && <PagedDialog.MenuItem id={5} label={<p className={styles.subItem}><SearchIcon style={{ fontSize: '18px' }} />Reference numbers</p>} />}
			{access?.pages.additionalDetails.visible && <PagedDialog.MenuItem id={6} label={<p className={styles.subItem}><TruckIcon style={{ fontSize: '18px' }} />Additional Details</p>} />}
		</PagedDialog.Menu>
	);
}
Menu.displayName = 'Menu';
